import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import {
  BookOpen,
  FileText,
  FolderOpen,
  MoreHorizontal,
  Pin,
  PinOff,
  Share2,
  Tag as TagIcon,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Skeleton } from "../../components/ui/skeleton";
import { type CollectionItem, api } from "../../lib/api";
import { formatRelative } from "../../lib/utils";
import { InlineChat } from "../chat/InlineChat";

/**
 * B2.3 — CollectionView reorganizada en 3 columnas como en DocuV2-UI:
 *  - Izquierda: tree de fuentes agrupadas (Documentos / Apuntes / Otros).
 *  - Centro: blocks de la colección con header tipo doc + acciones.
 *  - Derecha: InlineChat scoped a la colección.
 *
 * Notas inline editables (col-tree-section "Apuntes manuales" + dividers
 * "Añadir aquí") llegan en B4 cuando el schema de collection_items soporte
 * `inline_note`.
 */
export function CollectionView() {
  const { slug } = useParams({ strict: false }) as { slug: string };
  const [filter, setFilter] = useState<"all" | "document" | "notebook">("all");
  const qc = useQueryClient();

  const collection = useQuery({
    queryKey: ["collection", slug],
    queryFn: () => api.getCollection(slug),
    staleTime: 30_000,
  });

  // C5 — añadir inline_note
  const addInlineNote = useMutation({
    mutationFn: ({ orderIndex }: { orderIndex: number }) =>
      api.addCollectionMember(slug, {
        item_type: "inline_note",
        inline_text: "",
        order_index: orderIndex,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["collection-items", slug] });
      void qc.invalidateQueries({ queryKey: ["collection", slug] });
      toast.success("Nota añadida");
    },
    onError: (err) => toast.error(`No se pudo añadir: ${String(err)}`),
  });

  // C5 — editar inline_note
  const updateInline = useMutation({
    mutationFn: ({ id, text }: { id: string; text: string }) =>
      api.patchCollectionMember(slug, id, { inline_text: text }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["collection-items", slug] });
    },
    onError: (err) => toast.error(`No se pudo guardar: ${String(err)}`),
  });

  // C5 — eliminar miembro
  const removeMember = useMutation({
    mutationFn: (id: string) => api.removeCollectionMember(slug, id),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["collection-items", slug] });
      void qc.invalidateQueries({ queryKey: ["collection", slug] });
      toast.success("Item eliminado");
    },
    onError: (err) => toast.error(`No se pudo eliminar: ${String(err)}`),
  });

  // B5.1 — toggle pin (optimistic). Sidebar listens to ["collections"].
  const togglePin = useMutation({
    mutationFn: (next: boolean) => api.updateCollection(slug, { pinned: next }),
    onSuccess: (_data, next) => {
      void qc.invalidateQueries({ queryKey: ["collection", slug] });
      void qc.invalidateQueries({ queryKey: ["collections"] });
      toast.success(next ? "Colección fijada" : "Colección desfijada");
    },
    onError: (err) => toast.error(`No se pudo actualizar: ${String(err)}`),
  });

  const items = useQuery({
    queryKey: ["collection-items", slug, filter],
    queryFn: () => api.getCollectionItems(slug, { type: filter }),
    enabled: !!slug,
    staleTime: 10_000,
  });

  if (collection.isLoading) {
    return (
      <div className="mx-auto max-w-5xl px-6 py-6">
        <Skeleton className="mb-4 h-10 w-1/2" />
        <Skeleton className="mb-2 h-5 w-1/3" />
      </div>
    );
  }

  if (!collection.data) {
    return (
      <div className="mx-auto max-w-5xl px-6 py-6 text-center text-(--text-tertiary)">
        Colección no encontrada.
      </div>
    );
  }

  const col = collection.data;
  const list = items.data?.items ?? [];

  // Group items by kind for the left tree
  const docs = list.filter((i) => i.item_type === "document");
  const notebooks = list.filter((i) => i.item_type === "notebook");
  const others = list.filter((i) => i.item_type !== "document" && i.item_type !== "notebook");

  return (
    <div className="flex h-full overflow-hidden">
      {/* Left: sources tree */}
      <aside
        data-testid="collection-tree"
        className="hidden w-64 shrink-0 flex-col overflow-y-auto border-r border-(--border-subtle) bg-(--bg-elevated) p-3 md:flex"
      >
        <header className="mb-3 flex items-center justify-between">
          <span className="text-[10px] font-mono uppercase tracking-wider text-(--text-tertiary)">
            Fuentes
          </span>
          <span className="text-[10px] font-mono text-(--text-tertiary)">{list.length}</span>
        </header>

        {col.tags.length > 0 && (
          <div className="mb-3">
            <h3 className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-(--text-tertiary)">
              Etiquetas
            </h3>
            <div className="flex flex-wrap gap-1">
              {col.tags.map((t) => (
                <Link
                  key={t}
                  to="/collections"
                  search={{ tag: t }}
                  className="inline-flex items-center gap-1 rounded-full bg-(--bg-canvas) px-2 py-0.5 text-[11px] text-(--text-secondary) hover:bg-(--bg-hover) no-underline"
                >
                  <TagIcon size={10} /> {t}
                </Link>
              ))}
            </div>
          </div>
        )}

        <TreeSection title="Documentos" count={docs.length} defaultOpen>
          {docs.map((it) => (
            <TreeItem
              key={it.membership_id}
              icon={<FileText size={11} />}
              label={it.doc_title ?? it.item_id}
              meta={it.doc_path ?? ""}
              href={`/docs/${it.item_id}`}
            />
          ))}
        </TreeSection>
        {notebooks.length > 0 && (
          <TreeSection title="Apuntes" count={notebooks.length} defaultOpen>
            {notebooks.map((it) => (
              <TreeItem
                key={it.membership_id}
                icon={<BookOpen size={11} />}
                label={it.notebook_title ?? it.item_id}
                href={`/notebooks/${it.item_id}`}
              />
            ))}
          </TreeSection>
        )}
        {others.length > 0 && (
          <TreeSection title="Otros" count={others.length} defaultOpen={false}>
            {others.map((it) => (
              <TreeItem key={it.membership_id} icon={<FolderOpen size={11} />} label={it.item_id} />
            ))}
          </TreeSection>
        )}

        <div className="mt-3">
          <h3 className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-(--text-tertiary)">
            Filtro
          </h3>
          <div className="flex flex-wrap gap-1">
            {(["all", "document", "notebook"] as const).map((k) => (
              <button
                type="button"
                key={k}
                onClick={() => setFilter(k)}
                data-testid={`collection-filter-${k}`}
                className={
                  filter === k
                    ? "rounded-full bg-(--accent-500) px-2 py-0.5 text-[11px] text-white"
                    : "rounded-full bg-(--bg-canvas) px-2 py-0.5 text-[11px] hover:bg-(--bg-hover)"
                }
              >
                {k === "all" ? "Todos" : k === "document" ? "Documentos" : "Apuntes"}
              </button>
            ))}
          </div>
        </div>

        <footer className="mt-auto text-xs text-(--text-tertiary)">
          <p>
            slug: <code className="rounded bg-(--bg-canvas) px-1">{col.slug}</code>
          </p>
          <p>Actualizada {formatRelative(col.updated_at)}</p>
        </footer>
      </aside>

      {/* Centre: collection living-doc */}
      <main
        className="flex flex-1 flex-col overflow-y-auto px-6 py-6"
        data-testid="collection-main"
      >
        <header className="mb-6">
          <div className="text-xs text-(--text-tertiary)">
            <Link to="/collections" className="hover:underline">
              Colecciones
            </Link>{" "}
            › <span data-testid="collection-breadcrumb">{col.title}</span>
          </div>
          <h1 className="mt-2 text-3xl font-semibold leading-tight text-(--text-primary)">
            {col.title}
          </h1>
          {col.description && (
            <p className="mt-2 text-sm text-(--text-secondary)">{col.description}</p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-(--text-tertiary)">
            <Badge variant="default" className="text-[10px]">
              scope: colección
            </Badge>
            <Badge variant="muted" className="text-[10px]">
              {col.aggregates.items_total} items
            </Badge>
            <Badge variant="outline" className="text-[10px]">
              {col.visibility}
            </Badge>
            <span>actualizada {formatRelative(col.updated_at)}</span>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            <Button
              variant="secondary"
              size="sm"
              data-testid="collection-action-pin"
              data-pinned={col.pinned === 1 ? "true" : "false"}
              onClick={() => togglePin.mutate(col.pinned !== 1)}
              disabled={togglePin.isPending}
            >
              {col.pinned === 1 ? (
                <>
                  <PinOff size={13} /> Desfijar
                </>
              ) : (
                <>
                  <Pin size={13} /> Fijar
                </>
              )}
            </Button>
            <Button variant="secondary" size="sm">
              <Share2 size={13} /> Compartir
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() =>
                toast.message(
                  "Añadir documentos/notebooks: usa el menú '→ Apuntes' o el dialog desde búsqueda",
                )
              }
            >
              <MoreHorizontal size={13} />
            </Button>
          </div>
        </header>

        {items.isLoading && (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-24 w-full" />
            ))}
          </div>
        )}

        {!items.isLoading && list.length === 0 && (
          <div
            data-testid="collection-empty"
            className="rounded-lg border border-dashed border-(--border-strong) bg-(--bg-elevated) p-8 text-center text-sm text-(--text-tertiary)"
          >
            <h3 className="mb-1 text-base font-semibold text-(--text-primary)">Colección vacía</h3>
            <p>
              Añade documentos o apuntes desde la búsqueda o desde el menú "→ Apuntes" de cualquier
              selección.
            </p>
          </div>
        )}

        <ul className="space-y-3" data-testid="collection-items">
          {list.map((item, idx) => (
            <li key={item.membership_id}>
              <CollectionItemBlock
                item={item}
                onUpdateInline={(text) => updateInline.mutate({ id: item.membership_id, text })}
                onRemove={() => removeMember.mutate(item.membership_id)}
              />
              <InlineNoteDivider onAdd={() => addInlineNote.mutate({ orderIndex: idx + 1 })} />
            </li>
          ))}
          {list.length > 0 && list[0] && list[0].item_type !== "inline_note" && (
            <InlineNoteDivider onAdd={() => addInlineNote.mutate({ orderIndex: 0 })} />
          )}
        </ul>
      </main>

      {/* Right: scoped chat */}
      <aside className="hidden w-80 shrink-0 flex-col border-l border-(--border-subtle) bg-(--bg-elevated) lg:flex">
        <InlineChat
          scope="collection"
          scopeId={col.slug}
          scopeLabel={`col: ${col.title}`}
          filters={{ collection_slug: col.slug }}
        />
      </aside>
    </div>
  );
}

function CollectionItemBlock({
  item,
  onUpdateInline,
  onRemove,
}: {
  item: CollectionItem;
  onUpdateInline: (text: string) => void;
  onRemove: () => void;
}) {
  // C5 — render inline_note como un editor de notas, no como link.
  if (item.item_type === "inline_note") {
    return (
      <InlineNoteBlock
        membershipId={item.membership_id}
        text={item.inline_text ?? ""}
        onSave={onUpdateInline}
        onRemove={onRemove}
      />
    );
  }
  const isDoc = item.item_type === "document";
  const isNb = item.item_type === "notebook";
  const Icon = isDoc ? FileText : isNb ? BookOpen : FolderOpen;
  const title = item.doc_title ?? item.notebook_title ?? item.item_id;
  const subtitle = item.doc_path;
  const href = isDoc ? `/docs/${item.item_id}` : isNb ? `/notebooks/${item.item_id}` : undefined;

  const body = (
    <>
      <header className="flex items-center gap-2 mb-1.5 text-[11px] text-(--text-tertiary)">
        <span className="inline-flex items-center gap-1 rounded-full bg-(--bg-canvas) border border-(--border-subtle) px-2 py-0.5 font-mono uppercase tracking-wider text-(--text-secondary)">
          <Icon size={10} />
          {isDoc ? "Documento" : isNb ? "Apunte" : item.item_type}
        </span>
        {item.is_primary === 1 && (
          <Badge variant="muted" className="text-[10px]">
            primario
          </Badge>
        )}
        <span className="ml-auto whitespace-nowrap font-mono">
          {formatRelative(item.created_at)}
        </span>
      </header>
      <h3 className="text-base font-semibold text-(--text-primary)">{title}</h3>
      {subtitle && (
        <p className="mt-0.5 font-mono text-[11px] text-(--text-tertiary)">{subtitle}</p>
      )}
    </>
  );

  if (href) {
    return (
      <Link
        to={href}
        className="block rounded-lg border border-(--border-subtle) bg-(--bg-elevated) p-4 no-underline hover:border-(--border-default) hover:bg-(--bg-hover) transition-colors"
        data-testid="collection-item-block"
      >
        {body}
      </Link>
    );
  }
  return (
    <article
      className="rounded-lg border border-(--border-subtle) bg-(--bg-elevated) p-4"
      data-testid="collection-item-block"
    >
      {body}
    </article>
  );
}

function TreeSection({
  title,
  count,
  defaultOpen,
  children,
}: {
  title: string;
  count: number;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = useState(defaultOpen ?? true);
  return (
    <section className="mb-3" data-testid="collection-tree-section">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center justify-between rounded px-1 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-(--text-secondary) hover:bg-(--bg-hover)"
      >
        <span className="flex items-center gap-1">
          <span
            style={{
              display: "inline-block",
              transform: open ? "rotate(90deg)" : "rotate(0deg)",
              transition: "transform 120ms",
            }}
          >
            ▶
          </span>
          {title}
        </span>
        <span className="font-mono text-[10px] text-(--text-tertiary)">{count}</span>
      </button>
      {open && <ul className="mt-1 space-y-0.5">{children}</ul>}
    </section>
  );
}

function TreeItem({
  icon,
  label,
  meta,
  href,
}: {
  icon: React.ReactNode;
  label: string;
  meta?: string;
  href?: string;
}) {
  const content = (
    <>
      <span className="text-(--text-tertiary)">{icon}</span>
      <span className="flex-1 min-w-0">
        <span className="block truncate text-[12px] text-(--text-primary)">{label}</span>
        {meta && (
          <span className="block truncate font-mono text-[10px] text-(--text-tertiary)">
            {meta}
          </span>
        )}
      </span>
    </>
  );
  if (href) {
    return (
      <li>
        <a
          href={href}
          className="flex items-start gap-1.5 rounded px-1.5 py-1 no-underline hover:bg-(--bg-hover)"
        >
          {content}
        </a>
      </li>
    );
  }
  return <li className="flex items-start gap-1.5 rounded px-1.5 py-1">{content}</li>;
}

/**
 * C5 — Bloque de inline_note dentro de una colección.
 * Editor autosave (debounced 700ms + on-blur).
 */
function InlineNoteBlock({
  membershipId,
  text,
  onSave,
  onRemove,
}: {
  membershipId: string;
  text: string;
  onSave: (next: string) => void;
  onRemove: () => void;
}) {
  const [draft, setDraft] = useState(text);
  const lastSavedRef = useRef(text);

  // Sync if server returns canonical text
  useEffect(() => {
    if (text !== lastSavedRef.current) {
      setDraft(text);
      lastSavedRef.current = text;
    }
  }, [text]);

  // Debounced auto-save
  // biome-ignore lint/correctness/useExhaustiveDependencies: onSave is stable from useMutation
  useEffect(() => {
    if (draft === lastSavedRef.current) return;
    const id = window.setTimeout(() => {
      lastSavedRef.current = draft;
      onSave(draft);
    }, 700);
    return () => window.clearTimeout(id);
  }, [draft]);

  return (
    <article
      data-testid="collection-inline-note"
      data-membership-id={membershipId}
      className="group rounded-lg border border-(--accent-100) bg-(--accent-50) p-4"
    >
      <header className="mb-1.5 flex items-center gap-2 text-[11px] text-(--text-tertiary)">
        <span className="inline-flex items-center gap-1 rounded-full bg-(--bg-canvas) border border-(--border-subtle) px-2 py-0.5 font-mono uppercase tracking-wider">
          <Pin size={10} /> Nota
        </span>
        <button
          type="button"
          onClick={onRemove}
          data-testid="collection-inline-note-remove"
          className="ml-auto text-(--text-tertiary) opacity-0 group-hover:opacity-100 hover:text-(--error)"
          aria-label="Eliminar nota"
        >
          ✕
        </button>
      </header>
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={() => {
          if (draft !== lastSavedRef.current) {
            lastSavedRef.current = draft;
            onSave(draft);
          }
        }}
        placeholder="Escribe una nota dentro de esta colección…"
        rows={3}
        data-testid="collection-inline-note-editor"
        className="w-full resize-y bg-transparent text-sm leading-relaxed text-(--text-primary) outline-none"
      />
    </article>
  );
}

/**
 * C5 — Divider "Añadir nota aquí" entre items de la colección.
 */
function InlineNoteDivider({ onAdd }: { onAdd: () => void }) {
  return (
    <div
      className="flex items-center justify-center py-1.5"
      data-testid="collection-inline-divider"
    >
      <button
        type="button"
        onClick={onAdd}
        data-testid="collection-inline-add"
        className="inline-flex items-center gap-1 rounded-full border border-dashed border-(--border-default) px-3 py-0.5 text-[10px] font-mono uppercase tracking-wider text-(--text-tertiary) hover:text-(--accent-600) hover:border-(--accent-400) transition"
      >
        + Añadir nota aquí
      </button>
    </div>
  );
}
